import { ProgramTask } from '@cibo/core'
import { AuthUserPermission, Can } from '@cibo/profile'
import { CircularProgress, IconButton, SvgIcon, Tooltip } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { type MouseEvent, useCallback, useState } from 'react'
import { PROGRAMS_QUERY_KEY } from '../../queries/queryKey'
import { useProgramEngagementContext } from '../../context'
import { createAxiosStepOverClient } from './axiosStepOverClient'
import { StepOverResult, stepOverTask } from './stepOverTask'

/**
 * VS Code debug step-over icon (codicon: debug-step-over).
 */
const StepOverIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <path
      fill="currentColor"
      d="M9.99993 13C9.99993 14.103 9.10293 15 7.99993 15C6.89693 15 5.99993 14.103 5.99993 13C5.99993 11.897 6.89693 11 7.99993 11C9.10293 11 9.99993 11.897 9.99993 13ZM13.2499 2C12.8359 2 12.4999 2.336 12.4999 2.75V4.027C11.3829 2.759 9.75993 2 7.99993 2C5.03293 2 2.47993 4.211 2.06093 7.144C2.00193 7.554 2.28793 7.934 2.69793 7.993C2.73393 7.999 2.76993 8.001 2.80493 8.001C3.17193 8.001 3.49293 7.731 3.54693 7.357C3.86093 5.159 5.77593 3.501 8.00093 3.501C9.52993 3.501 10.9199 4.264 11.7439 5.501H9.75093C9.33693 5.501 9.00093 5.837 9.00093 6.251C9.00093 6.665 9.33693 7.001 9.75093 7.001H13.2509C13.6649 7.001 14.0009 6.665 14.0009 6.251V2.751C14.0009 2.337 13.6649 2.001 13.2509 2.001L13.2499 2Z"
    />
  </SvgIcon>
)

type StepOverMenuButtonProps = {
  task: ProgramTask
  taskId: string
  isComplete: boolean
}

export const StepOverMenuButton = ({ task, taskId, isComplete }: StepOverMenuButtonProps) => {
  const { engagement, fieldModels, engagementId } = useProgramEngagementContext()
  const queryClient = useQueryClient()
  const [pending, setPending] = useState(false)
  const [result, setResult] = useState<StepOverResult | null>(null)

  const handleStepOver = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (!engagementId) return

      setPending(true)
      setResult(null)

      const fieldResourceIds = fieldModels.data?.items.map(f => f.resourceId) ?? []
      const client = createAxiosStepOverClient()

      const res = await stepOverTask(client, {
        engagementId,
        taskId,
        ruleType: task.rule.type,
        ruleParams: task.rule.params as Record<string, unknown>,
        fieldResourceIds,
      })

      setResult(res)
      setPending(false)

      if (res.status === 'success') {
        engagement.refetch()
        queryClient.invalidateQueries({
          queryKey: [PROGRAMS_QUERY_KEY.TASK_DATA, { engagementId, taskId }],
        })
      }
    },
    [engagementId, taskId, task, fieldModels.data, queryClient]
  )

  if (isComplete) return null

  const tooltipTitle = result ? `${result.status}: ${result.message}` : `Step over "${taskId}"`

  const color =
    result?.status === 'success' ? 'success' : result?.status === 'error' ? 'error' : 'default'

  return (
    <Can useAny={[AuthUserPermission.DEBUG_USER]}>
      <Tooltip title={tooltipTitle}>
        <span>
          <IconButton
            size="small"
            color={result ? color : 'default'}
            onClick={handleStepOver}
            disabled={pending}
            data-testid={`step-over-task-${taskId}`}
            sx={{ p: 0.25, opacity: 0.7, '&:hover': { opacity: 1 } }}
          >
            {pending ? <CircularProgress size={14} /> : <StepOverIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </span>
      </Tooltip>
    </Can>
  )
}
